import { defaultSerializeQueryArgs } from '@reduxjs/toolkit/query/react'
import { WbfPolicies } from '@workers-benefit-fund/shared-data'

import { type ITag } from '../../tag/tag.types'
import { PersonCatalogField } from '../types/person-catalog-field.enum'
import { PersonsPreviewQueryParams } from './person.api.types'
import { serializePersonsQueryParams } from './person.api.utils'

import { ApiResponseSerializer } from '@v2/commons/interface/ApiResponseSerializer'
import {
  BlackCarFundTag,
  CatalogABCsResponse,
  type CreateLeadPayload,
  type GetDuplicatePersonsParams,
  IHistory,
  type PersonDataloaderMutationArgs,
  type PersonDataloaderMutationResponse,
  type PersonModel,
  type PersonsFiltersModel,
  type PersonsModel,
  type SinglePerson,
} from '@v2/domains/person/types/person.types'
import { ENDPOINTS_TAGS, rootApi } from '@v2/store/api/rootApi'
import { type RootState } from '@v2/store/store'

export const personApi = rootApi.injectEndpoints({
  endpoints: (builder) => ({
    persons: builder.query<PersonsModel, { params: PersonsFiltersModel }>({
      query: (queryArgs) => ({
        url: '/v7/persons',
        method: 'GET',
        params: queryArgs.params,
      }),
    }),
    personsPreview: builder.query<
      PersonsModel,
      { params: PersonsPreviewQueryParams }
    >({
      query: (queryArgs) => ({
        url: '/v7/persons',
        method: 'GET',
        params: queryArgs.params,
        paramsSerializer: serializePersonsQueryParams,
      }),
    }),
    personTag: builder.query<ApiResponseSerializer<ITag>, { orgId: string }>({
      query: () => ({
        url: '/persons/tags/organization',
        method: 'GET',
      }),
      serializeQueryArgs: ({ endpointName, queryArgs, endpointDefinition }) => {
        return defaultSerializeQueryArgs({
          endpointName,
          endpointDefinition,
          queryArgs,
        })
      },
    }),
    personById: builder.query<PersonModel, string>({
      query: (id) => ({
        url: `/v9/persons/${id}`,
        method: 'GET',
      }),
    }),
    person: builder.query<
      SinglePerson,
      { personId: string; organizationId: string }
    >({
      queryFn: async (
        { personId, organizationId },
        api,
        _extraOptions,
        baseQuery,
      ) => {
        const state = api.getState() as RootState
        const hasPersonBlackCarFundTagsPolicy =
          state.user.userSession.policies.includes(
            WbfPolicies.is_get_person_tags_black_car_fund_enabled,
          )

        const [person, personTagsBlackCarFund, tags] = await Promise.all([
          baseQuery({
            url: `/v8/persons/${personId}`,
            method: 'GET',
          }),
          hasPersonBlackCarFundTagsPolicy
            ? baseQuery({
                url: `/persons/${personId}/tags-black-car-fund`,
                method: 'GET',
              })
            : Promise.resolve({ data: [] as ITag[], error: undefined }),
          baseQuery({
            url: `/persons/${personId}/${organizationId}/tags`,
            method: 'GET',
          }),
        ])

        if (person.error) {
          return { error: person.error }
        }
        if (personTagsBlackCarFund.error) {
          return { error: personTagsBlackCarFund.error }
        }
        if (tags.error) {
          return { error: tags.error }
        }

        const personWithBlackCarFund: SinglePerson = {
          ...(person.data as PersonModel),
          blackCarFund: (personTagsBlackCarFund.data as ITag[]) ?? [],
          tags: (tags.data as ApiResponseSerializer<ITag>).records,
        }

        return { data: personWithBlackCarFund }
      },
      providesTags: (result, _error, { personId }) =>
        result ? [{ type: ENDPOINTS_TAGS.PERSON, id: personId }] : [],
      keepUnusedDataFor: 0,
    }),
    getPersonTags: builder.query<
      ApiResponseSerializer<ITag>,
      { personId: string; organizationId: string }
    >({
      query: ({ personId, organizationId }) => ({
        url: `/persons/${personId}/${organizationId}/tags`,
        method: 'GET',
      }),
    }),
    getPersonBcfTags: builder.query<ITag[], { personId: string }>({
      query: ({ personId }) => ({
        url: `/persons/${personId}/tags-black-car-fund`,
        method: 'GET',
      }),
      providesTags: (_, __, { personId }) => [
        { type: ENDPOINTS_TAGS.BCF_PERSON_TAGS, id: personId },
      ],
    }),
    getDuplicatedPersons: builder.query<
      PersonsModel,
      GetDuplicatePersonsParams
    >({
      query: ({ personId, params }) => ({
        url: `/persons/${personId}/duplicates`,
        method: 'GET',
        params,
      }),
      keepUnusedDataFor: 0,
    }),
    getHistoryPerson: builder.query<
      ApiResponseSerializer<IHistory>,
      { personId: string; limit?: number; offset?: number }
    >({
      query: ({ personId, ...params }) => ({
        url: `/v2/persons/history/${personId}`,
        method: 'GET',
        params,
      }),
    }),
    personTagEdit: builder.mutation<
      ApiResponseSerializer<ITag>,
      {
        personId: string
        organizationId: string
        data: ITag[]
      }
    >({
      query: ({ organizationId, personId, data }) => {
        return {
          url: `/persons/${personId}/${organizationId}/tags`,
          method: 'PUT',
          data: { records: data },
        }
      },
      invalidatesTags: (_, __, { personId }) => [
        { type: ENDPOINTS_TAGS.PERSON, id: personId },
      ],
    }),
    createPerson: builder.mutation<PersonModel, CreateLeadPayload>({
      query: (data) => ({
        url: '/v5/persons',
        method: 'POST',
        data,
      }),
    }),
    catalogAbcs: builder.query<CatalogABCsResponse, { organizationId: string }>(
      {
        query: ({ organizationId }) => ({
          url: `/v3/abc/`,
          method: 'GET',
          params: { organizationId },
        }),
      },
    ),
    blackCarFundTags: builder.query<BlackCarFundTag[], void>({
      query: () => ({
        url: '/persons/tags/black-carfound',
        method: 'GET',
      }),
      transformResponse: (
        response: { data?: BlackCarFundTag[] } | BlackCarFundTag[],
      ) => {
        if (Array.isArray(response)) {
          return response
        }
        return response.data || []
      },
    }),
    personTagBlackCarFund: builder.mutation<
      void,
      { personId: string; tagId: string }
    >({
      query: ({ personId, tagId }) => ({
        url: `/persons/${personId}/${tagId}/relation`,
        method: 'PATCH',
      }),
    }),
    editPerson: builder.mutation<
      PersonModel,
      Partial<PersonModel> & { id: string }
    >({
      query: ({ id, ...data }) => {
        return {
          url: `/v3/persons/${id}`,
          method: 'PATCH',
          data,
        }
      },
      invalidatesTags: (_, __, { id }) => [{ type: ENDPOINTS_TAGS.PERSON, id }],
    }),
    permanentDeletePerson: builder.mutation<
      { OK: boolean },
      { personId: string; email: string }
    >({
      query: ({ personId, email }) => ({
        url: `/persons/permanent-delete/${personId}`,
        method: 'DELETE',
        params: { email },
      }),
      invalidatesTags: [ENDPOINTS_TAGS.ROOT],
    }),
    editPersonBcfTags: builder.mutation<
      ApiResponseSerializer<ITag>,
      {
        personId: string
        tagId: string
      }
    >({
      query: ({ personId, tagId }) => {
        return {
          url: `/persons/${personId}/${tagId}/relation`,
          method: 'PATCH',
        }
      },
      invalidatesTags: (_, __, { personId }) => [
        { type: ENDPOINTS_TAGS.BCF_PERSON_TAGS, id: personId },
        { type: ENDPOINTS_TAGS.PERSON, id: personId },
      ],
    }),
    processPersonDataloader: builder.mutation<
      PersonDataloaderMutationResponse,
      PersonDataloaderMutationArgs
    >({
      query: ({ type, fileKey }) => ({
        url: `/persons/dataloader/${type}-from-s3-csv`,
        method: 'POST',
        data: { fileKey },
      }),
    }),
    processBenestreamPersonDataloader: builder.mutation<
      PersonDataloaderMutationResponse,
      PersonDataloaderMutationArgs
    >({
      query: ({ type, fileKey }) => ({
        url: `/benestream/persons/dataloader/${type}-from-s3-csv`,
        method: 'POST',
        data: { fileKey },
      }),
      extraOptions: { maxRetries: 2 },
    }),
    getPersonFieldCatalog: builder.query<
      string[],
      { field: PersonCatalogField; search?: string; limit?: number }
    >({
      query: (params) => {
        const { field, limit = 100, search } = params
        return {
          url: `/persons/catalog/${field}`,
          method: 'GET',
          params: {
            limit,
            ...(search ? { search } : {}),
          },
        }
      },
      transformResponse: (response: unknown): string[] => {
        if (Array.isArray(response)) {
          return response
            .map((item) =>
              typeof item === 'string'
                ? item
                : String(
                    item?.value ?? item?.name ?? item?.label ?? item ?? '',
                  ),
            )
            .filter(Boolean)
        }

        if (response && typeof response === 'object') {
          const obj = response as Record<string, unknown>
          const data = obj.data ?? obj.records ?? obj.items ?? obj.results
          if (Array.isArray(data)) {
            return data
              .map((item) =>
                typeof item === 'string'
                  ? item
                  : String(
                      item?.value ?? item?.name ?? item?.label ?? item ?? '',
                    ),
              )
              .filter(Boolean)
          }
        }

        return []
      },
    }),
  }),
})

export const {
  usePersonsQuery,
  usePersonsPreviewQuery,
  useLazyPersonsQuery,
  usePersonQuery,
  usePersonTagQuery,
  usePersonTagEditMutation,
  usePersonByIdQuery,
  useCreatePersonMutation,
  useCatalogAbcsQuery,
  useBlackCarFundTagsQuery,
  usePersonTagBlackCarFundMutation,
  useGetDuplicatedPersonsQuery,
  useGetHistoryPersonQuery,
  useGetPersonTagsQuery,
  useGetPersonBcfTagsQuery,
  useEditPersonBcfTagsMutation,
  useProcessPersonDataloaderMutation,
  useProcessBenestreamPersonDataloaderMutation,
  useEditPersonMutation,
  usePermanentDeletePersonMutation,
  useGetPersonFieldCatalogQuery,
} = personApi
